/**
 * Pure validation for the review desk. Deliberately free of any network import so it
 * can be unit-tested directly and reused anywhere.
 *
 * These rules mirror validate_options in backend/app/services/item_review_service.py.
 * They exist to answer the reviewer while they type. The backend re-checks everything
 * and wins any disagreement.
 */
import { OptionEdit, MIN_REJECT_NOTE } from './reviewTypes';

export const MIN_OPTIONS = 3;
export const MAX_OPTIONS = 5;

export function validateOptions(options: OptionEdit[]): string[] {
  const problems: string[] = [];
  if (options.length < MIN_OPTIONS || options.length > MAX_OPTIONS) {
    problems.push(`An item needs ${MIN_OPTIONS} to ${MAX_OPTIONS} options. This one has ${options.length}.`);
  }
  const ids = options.map((o) => (o.id || '').trim());
  if (ids.some((i) => !i)) problems.push('Every option needs a letter.');
  if (new Set(ids).size !== ids.length) problems.push('Option letters must be unique.');
  if (options.some((o) => !(o.text || '').trim())) problems.push('Every option needs text.');

  const keys = options.filter((o) => o.is_key);
  if (keys.length !== 1) {
    problems.push(keys.length === 0
      ? 'Mark one option as the correct answer.'
      : `Only one option can be correct. ${keys.length} are marked.`);
  }

  // The misconception is the product, not decoration: it becomes the explanation a
  // student reads after picking that option, so an unnamed distractor is unfinished.
  options
    .filter((o) => !o.is_key && !(o.misconception || '').trim())
    .forEach((o) => problems.push(
      `Option ${o.id} needs a named misconception. What does someone who picks it believe?`));

  return problems;
}

export function rejectionProblems(reasonCode: string | null, notes: string): string[] {
  const problems: string[] = [];
  if (!reasonCode) problems.push('Pick a reason.');
  if (notes.trim().length < MIN_REJECT_NOTE) {
    problems.push(`Say what is wrong in at least ${MIN_REJECT_NOTE} characters. This note is what fixes the generator.`);
  }
  return problems;
}

/** Extracts the editable stem from the block list the writer produced. */
export function stemOf(blocks: { type: string; md?: string }[]): string {
  const first = (blocks || []).find((b) => b.type === 'text');
  return first?.md ?? '';
}
